import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  MessageCircle, Shield, CheckCircle2, XCircle, Send, ExternalLink,
  KeyRound, PhoneCall, HelpCircle, Copy, RefreshCw, Building2, Save,
} from "lucide-react";

const emptyForm = () => ({
  enabled: false,
  phone_number_id: "",
  business_account_id: "",
  graph_api_version: "v25.0",
  template_name: "reminder_alert",
  language_code: "en_US",
  access_token: "",  // never sent from server; user re-types only when changing
});

const emptyCompany = () => ({
  name: "",
  address: "",
  gst_number: "",
  tan: "",
  pan: "",
  email: "",
  phone: "",
  logo_url: "",
  footer_note: "",
});

export default function Settings() {
  const [form, setForm] = useState(emptyForm());
  const [meta, setMeta] = useState({ token_saved: false, updated_at: null });
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [log, setLog] = useState([]);
  const [company, setCompany] = useState(emptyCompany());
  const [companyMeta, setCompanyMeta] = useState({ updated_at: null });
  const [savingCompany, setSavingCompany] = useState(false);

  const load = async () => {
    try {
      const [s, l, cp] = await Promise.all([
        api.get("/settings/whatsapp"),
        api.get("/settings/whatsapp/log").catch(() => ({ data: [] })),
        api.get("/settings/company").catch(() => ({ data: emptyCompany() })),
      ]);
      const { token_saved, updated_at, ...rest } = s.data;
      setForm((f) => ({ ...f, ...rest }));
      setMeta({ token_saved, updated_at });
      setLog(l.data);
      const { updated_at: cUpdatedAt, ...cRest } = cp.data || {};
      setCompany({ ...emptyCompany(), ...cRest });
      setCompanyMeta({ updated_at: cUpdatedAt });
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error(e?.response?.data?.detail || "Failed to load settings");
      }
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.access_token) delete payload.access_token; // keep saved token
      await api.put("/settings/whatsapp", payload);
      toast.success("WhatsApp settings saved");
      setForm((f) => ({ ...f, access_token: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const saveCompany = async () => {
    if (!(company.name || "").trim()) return toast.error("Company name is required");
    setSavingCompany(true);
    try {
      await api.put("/settings/company", company);
      toast.success("Company profile saved — reflected in every print");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingCompany(false); }
  };

  const sendTest = async () => {
    if (!testTo || !testTo.startsWith("+")) return toast.error("Enter recipient in E.164 (e.g. +919876543210)");
    setTesting(true);
    try {
      const r = await api.post("/settings/whatsapp/test", { to: testTo, parameters: ["Admin", "Test policy", "2026-12-31", "in 15 days"] });
      if (r.data.ok) toast.success(`Sent — Message ID ${r.data.message_id}`);
      else toast.error(`Failed: ${r.data.error} ${r.data.code ? `(code ${r.data.code})` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <div className="eyebrow">Settings</div>
        <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="settings-title">
          Notifications<br/>& <em className="italic">integrations.</em>
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-lg">
          Connect Meta WhatsApp Cloud API so reminders are pinged directly to admin, accounts and partners — even when they&apos;re logged out.
        </p>
      </div>

      {/* Company profile card — reflected in ALL prints */}
      <div className="border border-slate-200 bg-white rounded-md" data-testid="company-profile-card">
        <div className="flex items-center gap-3 p-5 border-b border-slate-100">
          <div className="p-2 rounded bg-red-50 text-red-700 border border-red-200">
            <Building2 className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900">Company profile</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Printed on every Bill, Invoice, Payment Advice, Report & Statement.
            </div>
          </div>
          {companyMeta.updated_at && (
            <span className="text-[10px] text-slate-400 font-mono-fin">
              Updated {companyMeta.updated_at.slice(0, 16).replace("T", " ")}
            </span>
          )}
        </div>
        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Company name <span className="text-red-600">*</span></Label>
              <Input value={company.name}
                onChange={(e) => setCompany({ ...company, name: e.target.value })}
                placeholder="e.g. MARKSAM Ventures LLP"
                className="mt-1.5" data-testid="co-name" />
            </div>
            <div className="sm:col-span-2">
              <Label>Registered address</Label>
              <Textarea rows={2} value={company.address}
                onChange={(e) => setCompany({ ...company, address: e.target.value })}
                placeholder="Street · City · State · PIN"
                className="mt-1.5" data-testid="co-address" />
            </div>
            <div>
              <Label>GST number</Label>
              <Input value={company.gst_number}
                onChange={(e) => setCompany({ ...company, gst_number: e.target.value.toUpperCase() })}
                placeholder="36ABCDE1234F1Z5"
                className="mt-1.5 font-mono-fin" data-testid="co-gst" />
            </div>
            <div>
              <Label>TAN</Label>
              <Input value={company.tan}
                onChange={(e) => setCompany({ ...company, tan: e.target.value.toUpperCase() })}
                placeholder="HYDA12345B"
                className="mt-1.5 font-mono-fin" data-testid="co-tan" />
            </div>
            <div>
              <Label>PAN <span className="text-slate-400 text-[10px]">(optional)</span></Label>
              <Input value={company.pan}
                onChange={(e) => setCompany({ ...company, pan: e.target.value.toUpperCase() })}
                placeholder="AAAAA0000A"
                className="mt-1.5 font-mono-fin" data-testid="co-pan" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={company.email}
                onChange={(e) => setCompany({ ...company, email: e.target.value })}
                placeholder="hello@company.com"
                className="mt-1.5" data-testid="co-email" />
            </div>
            <div>
              <Label>Phone <span className="text-slate-400 text-[10px]">(optional)</span></Label>
              <Input value={company.phone}
                onChange={(e) => setCompany({ ...company, phone: e.target.value })}
                placeholder="+91 98xxxxxxxx"
                className="mt-1.5" data-testid="co-phone" />
            </div>
            <div>
              <Label>Logo URL <span className="text-slate-400 text-[10px]">(optional)</span></Label>
              <Input value={company.logo_url}
                onChange={(e) => setCompany({ ...company, logo_url: e.target.value })}
                placeholder="https://.../logo.png"
                className="mt-1.5" data-testid="co-logo" />
            </div>
            <div className="sm:col-span-2">
              <Label>Print footer note <span className="text-slate-400 text-[10px]">(optional)</span></Label>
              <Input value={company.footer_note}
                onChange={(e) => setCompany({ ...company, footer_note: e.target.value })}
                placeholder='e.g. "Registered LLP · LLPIN xyz — Regd. Office: …"'
                className="mt-1.5" data-testid="co-footer" />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <Button onClick={saveCompany} disabled={savingCompany}
              className="rounded-md bg-red-600 hover:bg-red-700 text-white"
              data-testid="co-save">
              <Save className="h-4 w-4 mr-1.5" /> {savingCompany ? "Saving…" : "Save company profile"}
            </Button>
            <div className="text-[11px] text-slate-500 ml-2">
              This name replaces <span className="font-mono-fin text-slate-700">MARKSAM Ventures LLP</span> on every generated PDF & report.
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp integration card */}
      <div className="border border-slate-200 bg-white rounded-md">
        <div className="flex items-center gap-3 p-5 border-b border-slate-100">
          <div className="p-2 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
            <MessageCircle className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900">WhatsApp Meta Business Cloud API</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Send outbound template messages via Graph API {form.graph_api_version}
            </div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
            form.enabled
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-slate-50 text-slate-500 border-slate-200"
          }`} data-testid="wa-status">
            {form.enabled ? "Enabled" : "Disabled"}
          </span>
        </div>

        <div className="p-5 space-y-4">
          {/* Enable toggle */}
          <label className="flex items-center gap-2 text-sm text-slate-800">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="wa-enable" />
            Enable WhatsApp reminders
          </label>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Phone Number ID <span className="text-red-600">*</span></Label>
              <Input value={form.phone_number_id} onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
                placeholder="e.g. 123456789012345" className="mt-1.5 font-mono-fin" data-testid="wa-phone-id" />
            </div>
            <div>
              <Label>WhatsApp Business Account ID <span className="text-red-600">*</span></Label>
              <Input value={form.business_account_id} onChange={(e) => setForm({ ...form, business_account_id: e.target.value })}
                placeholder="e.g. 987654321098765" className="mt-1.5 font-mono-fin" data-testid="wa-waba-id" />
            </div>
            <div>
              <Label>Graph API Version</Label>
              <Input value={form.graph_api_version} onChange={(e) => setForm({ ...form, graph_api_version: e.target.value })} className="mt-1.5 font-mono-fin" />
            </div>
            <div>
              <Label>Language code</Label>
              <Input value={form.language_code} onChange={(e) => setForm({ ...form, language_code: e.target.value })} placeholder="en_US" className="mt-1.5 font-mono-fin" />
            </div>
            <div className="sm:col-span-2">
              <Label>Message template name <span className="text-slate-400 text-[10px]">(must be Approved in WhatsApp Manager)</span></Label>
              <Input value={form.template_name} onChange={(e) => setForm({ ...form, template_name: e.target.value })}
                placeholder="reminder_alert" className="mt-1.5 font-mono-fin" data-testid="wa-template" />
              <div className="text-[10px] text-slate-500 mt-1">
                Body should contain 4 params: <span className="font-mono-fin">{"{{1}} name"}</span>, <span className="font-mono-fin">{"{{2}} policy"}</span>, <span className="font-mono-fin">{"{{3}} due date"}</span>, <span className="font-mono-fin">{"{{4}} days left"}</span>.
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="flex items-center gap-1.5">
                <KeyRound className="h-3.5 w-3.5" />
                System User Access Token
                {meta.token_saved && (
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-sm">Saved · encrypted</span>
                )}
              </Label>
              <Input type="password" autoComplete="new-password"
                value={form.access_token}
                onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                placeholder={meta.token_saved ? "Leave blank to keep saved token" : "Paste your permanent access token"}
                className="mt-1.5 font-mono-fin" data-testid="wa-token" />
              <div className="text-[10px] text-slate-500 mt-1">
                Stored server-side using Fernet encryption; never returned to the browser.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <Button onClick={save} disabled={saving} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="wa-save">
              <Shield className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save securely"}
            </Button>
            <Button onClick={load} variant="outline" className="rounded-md" title="Reload"><RefreshCw className="h-4 w-4" /></Button>
            {meta.updated_at && (
              <div className="text-[11px] text-slate-500 ml-auto">
                Last updated {meta.updated_at.slice(0, 16).replace("T", " ")}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Send test message */}
      <div className="border border-slate-200 bg-white rounded-md p-5">
        <div className="flex items-center gap-2 mb-3">
          <PhoneCall className="h-4 w-4 text-slate-500" />
          <div className="text-sm font-semibold">Send test message</div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label>Recipient (E.164)</Label>
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="+919876543210" className="mt-1.5 font-mono-fin" data-testid="wa-test-to" />
          </div>
          <Button onClick={sendTest} disabled={testing || !form.enabled} className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-test-send">
            <Send className="h-4 w-4 mr-1.5" /> {testing ? "Sending…" : "Send test"}
          </Button>
        </div>
        {!form.enabled && (
          <p className="text-[11px] text-amber-700 mt-2">Enable WhatsApp above before sending a test message.</p>
        )}
        <p className="text-[11px] text-slate-500 mt-2">
          The test uses the template configured above with 4 sample parameters. The recipient must be on your test list for a new WhatsApp Business number.
        </p>
      </div>

      {/* How-to helper */}
      <div className="border border-slate-200 bg-slate-50 rounded-md p-5">
        <div className="flex items-center gap-2 mb-3">
          <HelpCircle className="h-4 w-4 text-slate-500" />
          <div className="text-sm font-semibold">How to obtain your Meta WhatsApp credentials</div>
        </div>
        <ol className="list-decimal ml-5 space-y-1.5 text-[12px] text-slate-700">
          <li>Sign up at <a className="text-red-600 hover:underline inline-flex items-center gap-1" href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer">developers.facebook.com/apps <ExternalLink className="h-3 w-3" /></a> and create a Business App.</li>
          <li>Add the <strong>WhatsApp</strong> product and complete API Setup to get a test phone number.</li>
          <li>Copy the <strong>Phone Number ID</strong> and <strong>WhatsApp Business Account ID</strong> from the API Setup screen.</li>
          <li>Create and submit a message template in <strong>WhatsApp Manager → Account tools → Message templates</strong>. Body must have 4 variables — <span className="font-mono-fin">{"{{1}}"}</span> name, <span className="font-mono-fin">{"{{2}}"}</span> policy title, <span className="font-mono-fin">{"{{3}}"}</span> due date, <span className="font-mono-fin">{"{{4}}"}</span> days left.</li>
          <li>Wait for the template to be <strong className="text-emerald-700">Approved</strong> (usually a few hours).</li>
          <li>Create a <strong>System User</strong> in Business Settings → Users → System Users, assign to your WABA, and generate a permanent token with <span className="font-mono-fin">whatsapp_business_messaging</span> + <span className="font-mono-fin">whatsapp_business_management</span> permissions.</li>
          <li>Paste the token above and enable.</li>
        </ol>
      </div>

      {/* Delivery log */}
      <div className="border border-slate-200 bg-white rounded-md">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-semibold">Delivery log <span className="text-[11px] font-mono-fin text-slate-400 ml-2">{log.length} entries</span></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-2 text-left">Time</th>
                <th className="p-2 text-left">To</th>
                <th className="p-2 text-left">Template</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-left">Message ID / Error</th>
              </tr>
            </thead>
            <tbody>
              {log.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-slate-500">No deliveries logged yet.</td></tr>
              )}
              {log.map((r) => (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`log-${r.id}`}>
                  <td className="p-2 text-slate-600 font-mono-fin">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-2 font-mono-fin">{r.to}</td>
                  <td className="p-2 text-slate-700">{r.template}</td>
                  <td className="p-2">
                    {r.status === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Sent</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600"><XCircle className="h-3 w-3" /> Failed</span>
                    )}
                  </td>
                  <td className="p-2 font-mono-fin text-[11px] text-slate-500 truncate max-w-[240px]">
                    {r.status === "sent" ? r.message_id : `${r.error}${r.error_code ? ` (${r.error_code})` : ""}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
